import * as path from 'node:path';
import { cliSimpleGit } from '../../cli-simple-git.js';
import { buildChangedFileRowsFromPaths } from './build-changed-file-rows.js';
import { enrichCommitFileRows } from './enrich-commit-file-rows.js';
import { collectChangedPaths, parseDiffOutput } from './parse-diff-output.js';
import { parentForCommitDiff } from './parent-for-commit-diff.js';
import { normalizeRepoPathRelativeToWorkspaceRoot } from '../repo/normalize-repo-path-relative-to-workspace-root.js';
import { RENAME_DIFF_ARGS } from './rename-diff-args.js';
import { dedupeChangedFileRows } from '../rows/dedupe-changed-file-rows.js';
import type { WorkingTreeChangeFileRow } from '../types.js';
import type { ListChangedFilesOptions } from './list-changed-files-options.js';

const COMMIT_FILES_CACHE_TTL_MS = 5 * 60_000;
const commitFilesCache = new Map<string, { rows: WorkingTreeChangeFileRow[]; expiresAt: number }>();

function commitFilesCacheKey(repoGitCwd: string, commitSha: string): string {
  return `${path.resolve(repoGitCwd)}:${commitSha}`;
}

/**
 * Files touched by a single commit vs its first parent (or empty tree for root commits).
 */
export async function listChangedFilesForCommit(
  repoGitCwd: string,
  repoPathRelativeToWorkspaceRoot: string,
  commitSha: string,
  options: ListChangedFilesOptions = {},
): Promise<WorkingTreeChangeFileRow[]> {
  const cacheKey = commitFilesCacheKey(repoGitCwd, commitSha);
  if (!options.includeFilePatches) {
    const now = Date.now();
    const cached = commitFilesCache.get(cacheKey);
    if (cached && cached.expiresAt > now) return cached.rows;
  }

  const g = cliSimpleGit(repoGitCwd);
  const normalizedRepoPathRelativeToWorkspaceRoot = normalizeRepoPathRelativeToWorkspaceRoot(
    repoPathRelativeToWorkspaceRoot,
  );

  const [nameStatusRaw, numstatRaw] = await Promise.all([
    g
      .raw(['diff-tree', '--no-commit-id', '--name-status', '-r', ...RENAME_DIFF_ARGS, commitSha])
      .catch(() => ''),
    g.raw(['show', '--numstat', '--format=format:', commitSha]).catch(() => ''),
  ]);

  const parsed = parseDiffOutput(String(nameStatusRaw), String(numstatRaw));
  const paths = collectChangedPaths({
    nameEntries: parsed.nameEntries,
    numByPath: parsed.numByPath,
  });

  let rows = await buildChangedFileRowsFromPaths({
    paths,
    repoPathRelativeToWorkspaceRoot: normalizedRepoPathRelativeToWorkspaceRoot,
    nameByPath: parsed.nameByPath,
    numByPath: parsed.numByPath,
    movedSourcePaths: parsed.movedSourcePaths,
  });

  rows = dedupeChangedFileRows(rows);

  if (options.includeFilePatches) {
    const parent = await parentForCommitDiff(g, commitSha);
    const range = `${parent}..${commitSha}`;
    await enrichCommitFileRows({
      rows,
      repoGitCwd,
      repoPathRelativeToWorkspaceRoot: normalizedRepoPathRelativeToWorkspaceRoot,
      range,
      commitSha,
      parentSha: parent,
      binaryByPath: parsed.binaryByPath,
    });
  }

  const finalRows = dedupeChangedFileRows(rows);
  finalRows.sort((a, b) => a.workspaceRelPath.localeCompare(b.workspaceRelPath));
  if (!options.includeFilePatches) {
    commitFilesCache.set(cacheKey, {
      rows: finalRows,
      expiresAt: Date.now() + COMMIT_FILES_CACHE_TTL_MS,
    });
  }
  return finalRows;
}
